import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, useEffect, useMemo, useReducer, useRef, useState} from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {GetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {
  getDefaultDataFrameColumnMapping,
  isDataFrameColumnMappingComplete,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import AnalysisTemplateSelector from 'analysis-tool/templates/AnalysisTemplateSelector';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {GetAnalysisTemplate} from 'analysis-tool/templates/graphql/__generated/GetAnalysisTemplate';
import {GET_ANALYSIS_TEMPLATE_QUERY} from 'analysis-tool/templates/graphql/GetAnalysisTemplate';
import AnalysisTemplateInputTableColumnMapper from 'analysis-tool/templates/table-mapping/AnalysisTemplateInputTableColumnMapper';
import useAnalysisTemplateSelectorState, {
  TemplateApplyStateType,
} from 'analysis-tool/templates/useAnalysisTemplateSelectorState';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {FileData, PaneKey, TaskState} from 'bulk-import/SpreadsheetBulkImportConstants';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Checkbox from 'ui-library/Checkbox';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {LabelStyle, Size} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepNavVariant} from 'ui-library/StepNav';

import {AddComponentButton, RemoveComponentButton} from '../RunSchemaEditorActionButton';
import {ClientAutomationOutputFileConfigSpec} from '../RunSchemaSpecConstants';
import {
  convertColumnTypeMetadatasToString,
  convertStringToColumnTypeMetadatas,
  DatasetColumnTypeMetadata,
  DatasetConfigModalState,
  INITIAL_STATE,
  PRIMITIVE_COLUMN_TYPES,
  PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME,
  PRIMITIVE_COLUMN_TYPE_TO_LEAF_TYPE,
  StepKey,
} from './DatasetConfigConstants';
import reducer, {DatasetConfigModalAction, DatasetConfigModalActionType} from './datasetConfigModalReducer';
import ImportColumnHeadersButton from './ImportColumnHeadersButton';

export interface DatasetConfigChanges {
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>;
  analysisTemplate: string | null;
}

export interface DatasetConfigModalConfiguration {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  goToUploadSpreadsheet: () => void;
  goToImportInstrumentData: () => void;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
}

export interface DatasetConfigModalProps extends DatasetConfigModalConfiguration {
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  uploadedFileData: FileData | null;
}

function columnTypeMetadatasToDataFrameColumnDefs(
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>
): Array<DataFrameColumnDef> {
  return columnTypeMetadatas
    .filter((columnTypeMetadata) => columnTypeMetadata.name)
    .map((columnTypeMetadata) => ({
      displayName: columnTypeMetadata.name!,
      systemName: columnTypeMetadata.name!,
      type: {leafType: PRIMITIVE_COLUMN_TYPE_TO_LEAF_TYPE[columnTypeMetadata.columnType ?? 'text']},
    }));
}

function getColumnMapping(state: DatasetConfigModalState): Record<string, string> {
  return Object.fromEntries(
    state.columnTypeMetadatas
      .filter((metadata) => metadata.name && metadata.templateColumnName)
      .map((metadata) => [metadata.templateColumnName!, metadata.name!])
  );
}

function getSelectedTemplateDataFrame(
  templateApplyState: TemplateApplyStateType
): GetAnalysisDataFrame | null {
  return (
    templateApplyState.inputTables?.find((table) => table.variable != null)?.templateLineageNode?.data
      .dataFrame ?? null
  );
}

function getSelectedTemplateDataFrameColumns(
  analysisTemplate: AnalysisTemplateGraphQL | null
): Array<DataFrameColumnDef> {
  return analysisTemplate?.dataFrameVariables[0].replaceDataFrameColumnsSpec ?? [];
}

function allColumnTypeMetadataSpecified(columnTypeMetadatas: Array<DatasetColumnTypeMetadata>): boolean {
  return columnTypeMetadatas.every(
    (metadata) => metadata.name && metadata.columnType && metadata.isRequired != null
  );
}

function getStepButtons(
  state: DatasetConfigModalState,
  dispatch: Dispatch<DatasetConfigModalAction>,
  onRequestSubmit: (configChanges: DatasetConfigChanges) => void | Promise<void>,
  templateApplyState: TemplateApplyStateType
): Array<ModalButton> {
  const columnMapping = getColumnMapping(state);

  if (!isAnalysisTemplatesEnabled()) {
    return [
      {
        text: 'Done',
        submit: true,
        onClick: async () => {
          await onRequestSubmit({
            columnTypeMetadatas: state.columnTypeMetadatas,
            analysisTemplate: state.analysisTemplate,
          });
        },
        disabled: !allColumnTypeMetadataSpecified(state.columnTypeMetadatas),
      },
    ];
  }

  if (state.currentStepKey === StepKey.COLUMNS) {
    return [
      {
        text: 'Next',
        submit: true,
        onClick: () => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.ANALYSIS_TEMPLATE,
            columnTypeMetadatas: state.columnTypeMetadatas,
          });
        },
        disabled: !allColumnTypeMetadataSpecified(state.columnTypeMetadatas),
      },
    ];
  } else {
    // StepKey.ANALYSIS_TEMPLATE case
    return [
      {
        text: 'Back',
        submit: false,
        onClick: () => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey: StepKey.COLUMNS,
          });
        },
      },
      {
        text: 'Done',
        submit: true,
        onClick: async () => {
          await onRequestSubmit({
            columnTypeMetadatas: state.columnTypeMetadatas,
            analysisTemplate: state.analysisTemplate,
          });
        },
        disabled: !isDataFrameColumnMappingComplete(
          getSelectedTemplateDataFrameColumns(templateApplyState.selectedTemplate),
          columnMapping
        ),
      },
    ];
  }
}

function getHelpText(currentStepKey: StepKey, isColumnMappingEditable: boolean): string {
  if (currentStepKey === StepKey.COLUMNS) {
    if (!isColumnMappingEditable) {
      return `When results are recorded, the dataset is automatically created from the result table generated by the run. The dataset's columns and corresponding data types are preconfigured based on the result schema set in the output file configuration.\nTo modify column mappings, update the result schema in the output file configuration.\nTo remove column mappings, uncheck the "Record dataset" box.`;
    }
    return `Select the columns you want to include in your dataset, and their corresponding data types.`;
  } else if (currentStepKey === StepKey.ANALYSIS_TEMPLATE) {
    return 'Optionally, select an analysis template to be automatically applied to the dataset generated by this run. The template selector is pre-filtered to show only templates with a single variable input.';
  } else {
    return '';
  }
}

function getStepsList(): Array<{key: StepKey; title: string}> {
  if (!isAnalysisTemplatesEnabled()) {
    return [
      {
        key: StepKey.COLUMNS,
        title: 'Set dataset columns',
      },
    ];
  }

  const stepsList: Array<{key: StepKey; title: string}> = [];
  stepsList.push({
    key: StepKey.COLUMNS,
    title: 'Set dataset columns',
  });
  stepsList.push({
    key: StepKey.ANALYSIS_TEMPLATE,
    title: 'Set analysis template (optional)',
  });
  return stepsList;
}

interface DatasetColumnConfigPageProps {
  columnTypeMetadatas: Array<DatasetColumnTypeMetadata>;
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  onAddColumnTypeMetadata: (columnTypeMetadata: DatasetColumnTypeMetadata) => void;
  onUpdateColumnTypeMetadata: (index: number, updatedColumnTypeMetadata: DatasetColumnTypeMetadata) => void;
  onUpdateColumnTypeMetadatas: (updatedColumnTypeMetadatas: Array<DatasetColumnTypeMetadata>) => void;
  onRemoveColumnTypeMetadata: (index: number) => void;
}

export function DatasetColumnConfigPage(props: DatasetColumnConfigPageProps): JSX.Element {
  const isEditable = !(
    props.outputFileConfig.shouldCreateDataset && props.outputFileConfig.resultSchemaInfo?.schema
  );

  return (
    <>
      <Row className={css(styles.headerRow)}>
        <Col md={5} className={css(styles.headerColumn)}>
          Column
        </Col>
        <Col md={4} className={css(styles.headerColumn)}>
          Column type
        </Col>
        <Col md={2} className={css(styles.headerColumn)}>
          <label>
            <IndeterminateCheckbox
              disabled={!isEditable || props.columnTypeMetadatas.length === 0}
              items={props.columnTypeMetadatas}
              selectedMap={(columnTypeMetadata: DatasetColumnTypeMetadata): boolean =>
                columnTypeMetadata.isRequired ?? true
              }
              onChange={() => {
                const anySelected = props.columnTypeMetadatas.some(
                  (columnTypeMetadata): boolean => columnTypeMetadata.isRequired ?? true
                );
                props.onUpdateColumnTypeMetadatas(
                  props.columnTypeMetadatas.map(
                    (columnTypeMetadata): DatasetColumnTypeMetadata => ({
                      ...columnTypeMetadata,
                      isRequired: !anySelected,
                    })
                  )
                );
              }}
            />
            <span>Required?</span>
          </label>
        </Col>
        <Col md={1} className={css(styles.headerColumn)} />
      </Row>
      {props.columnTypeMetadatas.map((columnTypeMetadata, index) => (
        <Row key={index} className={css(styles.row)}>
          <Col md={5} className={css(styles.column)}>
            {isEditable ? (
              <TextField
                label={`Column name ${index}`}
                labelStyle={LabelStyle.HIDDEN}
                value={columnTypeMetadata.name ?? ''}
                size={Size.MEDIUM}
                onChange={(e) => {
                  props.onUpdateColumnTypeMetadata(index, {
                    ...columnTypeMetadata,
                    name: e.target.value,
                  });
                }}
                placeholder="Column name"
              />
            ) : (
              columnTypeMetadata.name
            )}
          </Col>
          <Col md={4} className={css(styles.column)}>
            {isEditable ? (
              <SelectDropdown
                displayName={
                  columnTypeMetadata.columnType
                    ? PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[columnTypeMetadata.columnType]
                    : 'Choose column type...'
                }
                dataTestAttrs={{
                  component: 'SetColumnsMapFieldsSection',
                  element: 'selectDropdown',
                }}
                menu={
                  <SearchableMenu
                    selectedValue={columnTypeMetadata.columnType ?? 'select'}
                    onSelect={(value) => {
                      props.onUpdateColumnTypeMetadata(index, {...columnTypeMetadata, columnType: value});
                    }}
                    optionDatas={PRIMITIVE_COLUMN_TYPES.map(
                      (type: string): OptionData => ({
                        name: PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[type],
                        value: type,
                      })
                    )}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                  />
                }
              />
            ) : columnTypeMetadata.columnType ? (
              PRIMITIVE_COLUMN_TYPE_TO_DISPLAY_NAME[columnTypeMetadata.columnType]
            ) : (
              ''
            )}
          </Col>
          <Col md={2} className={css(styles.column)}>
            <Checkbox
              checked={columnTypeMetadata.isRequired ?? true}
              onChange={() => {
                const current = columnTypeMetadata.isRequired ?? true;
                props.onUpdateColumnTypeMetadata(index, {...columnTypeMetadata, isRequired: !current});
              }}
              disabled={!isEditable}
            />
          </Col>
          <Col md={1} className={css(styles.column)}>
            {isEditable && (
              <RemoveComponentButton
                label="Remove"
                onClick={() => {
                  props.onRemoveColumnTypeMetadata(index);
                }}
              />
            )}
          </Col>
        </Row>
      ))}
      {isEditable && (
        <Row className={css(styles.row)}>
          <Col md={6} className={css(styles.column)}>
            <AddComponentButton
              label="Add Column"
              onClick={() => {
                props.onAddColumnTypeMetadata({isRequired: true});
              }}
            />
            <a
              className={css(styles.headerColumn)}
              onClick={() => {
                props.onAddColumnTypeMetadata({isRequired: true});
              }}
            >
              Add Column
            </a>
          </Col>
        </Row>
      )}
    </>
  );
}

interface AnalysisTemplatePageProps {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  setColumnMapping: (columnMapping: Record<string, string>) => void;
  state: DatasetConfigModalState;
  templateApplyState: TemplateApplyStateType;
  setSelectedTemplate: (
    selectedTemplate: AnalysisTemplateGraphQL | null,
    shouldCallPostSelectFn?: boolean
  ) => void;
  setSelectedTemplateId: (selectedTemplateId: string) => void;
}

function filterTemplate(template: AnalysisTemplateSelectorTemplate): boolean {
  return template.dataFrameVariables.length === 1;
}

function AnalysisTemplatePage(props: AnalysisTemplatePageProps): JSX.Element {
  const {
    outputFileConfig,
    setColumnMapping,
    setSelectedTemplate,
    state,
    templateApplyState,
    setSelectedTemplateId,
  } = props;

  const shouldSkip = Boolean(templateApplyState.selectedTemplate || !outputFileConfig.analysisTemplate);
  const [queried, setQueried] = useState(shouldSkip);

  const queryResult = useSkippableBenchlingQuery<GetAnalysisTemplate>(GET_ANALYSIS_TEMPLATE_QUERY, {
    skip: queried || shouldSkip,
    variables: {analysisTemplateId: outputFileConfig.analysisTemplate},
  });

  useEffect(() => {
    if (!queried) {
      const queriedTemplate =
        queryResult.type === QueryResultType.SUCCESS && !isInaccessible(queryResult.data.analysisTemplate)
          ? queryResult.data.analysisTemplate
          : null;
      if (queriedTemplate != null) {
        setSelectedTemplate(queriedTemplate, false);
        setQueried(true);
      }
    }
  }, [queried, queryResult, setSelectedTemplate]);

  const inputTableColumns = columnTypeMetadatasToDataFrameColumnDefs(state.columnTypeMetadatas);
  const columnMapping = Object.fromEntries(
    state.columnTypeMetadatas
      .filter((metadata) => metadata.name && metadata.templateColumnName)
      .map((metadata) => [metadata.templateColumnName!, metadata.name!])
  );

  function setSelectedTemplateWithPostSelectFn(selectedTemplate: AnalysisTemplateGraphQL | null): void {
    setSelectedTemplate(selectedTemplate, true);
  }

  const templateSelector = !queried ? (
    <CenteredHorizontalThrobber />
  ) : (
    <AnalysisTemplateSelector
      templateApplyState={templateApplyState}
      onSelectId={setSelectedTemplateId}
      onSelect={setSelectedTemplateWithPostSelectFn}
      filterTemplate={filterTemplate}
      includeNone={true}
    />
  );
  const selectedTemplateDataFrame = getSelectedTemplateDataFrame(templateApplyState);
  const selectedTemplateDataFrameColumns = getSelectedTemplateDataFrameColumns(
    templateApplyState.selectedTemplate
  );

  return (
    <AnalysisTemplateInputTableColumnMapper
      columnMapping={columnMapping}
      setColumnMapping={setColumnMapping}
      inputTableColumns={inputTableColumns}
      inputTableColumnsHeader="Dataset column"
      inputTableDataFrameIdForPreview={null}
      inputTableSelector={templateSelector}
      inputTableSelectorLabel="Analysis template"
      templateTableColumnsHeader="Template column"
      templateTableDataFrame={selectedTemplateDataFrame}
      templateTableDataFrameColumns={selectedTemplateDataFrameColumns}
      templateTableLabel="Template table"
      isInputTableColumnUnique={true}
    />
  );
}

export default function DatasetConfigModal(props: DatasetConfigModalProps): JSX.Element {
  const initialColumnTypeMetadatas = convertStringToColumnTypeMetadatas(
    props.outputFileConfig.datasetConfigJson
  );
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL_STATE,
    analysisTemplate: props.outputFileConfig.analysisTemplate ?? null,
    columnTypeMetadatas: initialColumnTypeMetadatas,
  });

  // Make sure we only pull in columns based on spreadsheet upload once per file
  const importedFileColumns = useRef(false);
  useEffect(() => {
    if (props.uploadedFileData?.state === TaskState.COMPLETED && !importedFileColumns.current) {
      importedFileColumns.current = true;
      const newMetadatas: Array<DatasetColumnTypeMetadata> = props.uploadedFileData.columns.map((column) => ({
        name: column.name,
        // it's called 'decimal' in Benchling data typing, but 'float' here, so we need to convert
        columnType: column.dataType === 'decimal' ? 'float' : (column.dataType ?? 'text'),
        isRequired: true,
      }));
      dispatch({
        type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
        columnTypeMetadatas: newMetadatas,
      });
    }
  }, [props.uploadedFileData]);

  function setColumnMapping(columnMapping: Record<string, string>): void {
    const flippedColumnMapping: Record<string, string> = Object.fromEntries(
      Object.entries(columnMapping).map(([key, value]) => [value, key])
    );
    const newMetadatas: Array<DatasetColumnTypeMetadata> = [];
    for (const metadata of state.columnTypeMetadatas) {
      if (metadata.name && flippedColumnMapping[metadata.name]) {
        metadata.templateColumnName = flippedColumnMapping[metadata.name];
      } else {
        delete metadata.templateColumnName;
      }
      newMetadatas.push(metadata);
    }
    dispatch({
      type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
      columnTypeMetadatas: newMetadatas,
    });
  }

  function postSetSelectedTemplateFn(selectedTemplate: AnalysisTemplateGraphQL | null): void {
    const inputTableColumns = columnTypeMetadatasToDataFrameColumnDefs(state.columnTypeMetadatas);
    const newColumnMapping = getDefaultDataFrameColumnMapping(
      getSelectedTemplateDataFrameColumns(selectedTemplate),
      inputTableColumns
    );
    setColumnMapping(newColumnMapping);
  }

  const [templateApplyState, , setSelectedTemplateBase, setSelectedTemplateId] =
    useAnalysisTemplateSelectorState(props.outputFileConfig.name);

  function setSelectedTemplate(
    selectedTemplate: AnalysisTemplateGraphQL | null,
    shouldCallPostSelectFn?: boolean
  ): void {
    setSelectedTemplateBase(selectedTemplate);
    if (shouldCallPostSelectFn) {
      postSetSelectedTemplateFn(selectedTemplate);
    }
    dispatch({
      type: DatasetConfigModalActionType.UPDATE_ANALYSIS_TEMPLATE,
      analysisTemplate: selectedTemplate ? selectedTemplate.id : null,
    });
  }

  function onRequestSubmit(configChanges: DatasetConfigChanges): void {
    props.onUpdateConfig({
      ...props.outputFileConfig,
      analysisTemplate: configChanges.analysisTemplate,
      datasetConfigJson: convertColumnTypeMetadatasToString(configChanges.columnTypeMetadatas),
    });
    props.modalProps.onRequestClose();
  }

  const buttons = getStepButtons(state, dispatch, onRequestSubmit, templateApplyState);
  const steps = useMemo(() => getStepsList(), []);

  const goToImportInstrumentData: () => void = SpreadsheetBulkImportActions.goToPaneWithKey.bind(
    null,
    PaneKey.INSTRUMENT_DATA
  );
  const goToUploadSpreadsheet: () => void = SpreadsheetBulkImportActions.goToPaneWithKey.bind(
    null,
    PaneKey.UPLOAD_SPREADSHEET
  );

  const isColumnMappingEditable = !(
    props.outputFileConfig.shouldCreateDataset && props.outputFileConfig.resultSchemaInfo?.schema
  );

  return (
    <Modal
      buttons={buttons}
      closeableOnEscapeKey={false}
      mediumLarge={true}
      fullWidth={state.currentStepKey === StepKey.ANALYSIS_TEMPLATE}
      onRequestClose={props.modalProps.onRequestClose}
      title={props.modalProps.title}
    >
      <StepNav<StepKey>
        steps={steps}
        statusByStep={state.statusByStep}
        currentStepKey={state.currentStepKey}
        onUpdateCurrentStep={(nextStepKey: StepKey) => {
          dispatch({
            type: DatasetConfigModalActionType.UPDATE_CURRENT_STEP,
            nextStepKey,
          });
        }}
        variant={StepNavVariant.FLAT}
      />
      <p className={css(styles.helpText)}>{getHelpText(state.currentStepKey, isColumnMappingEditable)}</p>
      {state.columnTypeMetadatas.length === 0 && (
        <div className={css(styles.importColumnHeadersButton)}>
          <ImportColumnHeadersButton
            goToUploadSpreadsheet={goToUploadSpreadsheet}
            goToImportInstrumentData={
              props.outputFileConfig.transformDataType ? goToImportInstrumentData : undefined
            }
          />
        </div>
      )}
      {state.currentStepKey === StepKey.COLUMNS ? (
        <DatasetColumnConfigPage
          columnTypeMetadatas={state.columnTypeMetadatas}
          outputFileConfig={props.outputFileConfig}
          onAddColumnTypeMetadata={(columnTypeMetadata) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [...state.columnTypeMetadatas, columnTypeMetadata],
            });
          }}
          onUpdateColumnTypeMetadata={(index, updatedColumnTypeMetadata) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [
                ...state.columnTypeMetadatas.slice(0, index),
                updatedColumnTypeMetadata,
                ...state.columnTypeMetadatas.slice(index + 1),
              ],
            });
          }}
          onUpdateColumnTypeMetadatas={(updatedColumnTypeMetadatas) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: updatedColumnTypeMetadatas,
            });
          }}
          onRemoveColumnTypeMetadata={(index) => {
            dispatch({
              type: DatasetConfigModalActionType.UPDATE_COLUMN_TYPE_METADATAS,
              columnTypeMetadatas: [
                ...state.columnTypeMetadatas.slice(0, index),
                ...state.columnTypeMetadatas.slice(index + 1),
              ],
            });
          }}
        />
      ) : (
        <AnalysisTemplatePage
          outputFileConfig={props.outputFileConfig}
          setColumnMapping={setColumnMapping}
          state={state}
          templateApplyState={templateApplyState}
          setSelectedTemplateId={setSelectedTemplateId}
          setSelectedTemplate={setSelectedTemplate}
        />
      )}
    </Modal>
  );
}

export const styles = StyleSheet.create({
  column: {
    padding: '15px',
  },
  headerColumn: {
    marginBottom: '8px',
  },
  headerRow: {
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderBottomWidth: 1,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
  helpText: {
    fontStyle: 'italic',
    padding: '10px 0 20px',
    whiteSpace: 'pre-line',
  },
  importColumnHeadersButton: {
    marginTop: '-10px',
    paddingBottom: '20px',
  },
  row: {
    borderColor: Colors.GRAY_LIGHT,
    borderStyle: 'solid',
    borderWidth: 0,
    borderTopWidth: 1,
    ...FONT_PROXIMA_13,
    margin: 0,
  },
});
